import React, { useEffect, useState } from 'react';
import { useRouteMatch, Link, useHistory, generatePath } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Input, Icon, Dropdown, Popup, Checkbox, Table, DropdownProps, CheckboxProps, Dimmer, Loader, Message } from 'semantic-ui-react';
import ErrorMessageList from '../components/elements/ErrorMessageList';
import Button from '../components/elements/Button';
import API from '../utils/network';
import { isNullOrUndefined, generatePathForceParams, stringIsNullOrUndefinedOrBlank, removeElementFromArray } from '../utils/siteFunctions';
import { CollectionStatus } from '../models/GeneralModels';

const SearchPage = ({ showUploadFilesMessage = false, visible = true }: SearchPageProps) => {
	/// Hooks
	const match = useRouteMatch();
	const history = useHistory();

	/// Global State
	const pin = useSelector(state => state.ui.customer.pin);
	const secondaryColor = useSelector(state => state.ui.customer.secondaryColor);

	/// Local State
	const [ filtersOpen, setFiltersOpen ] = useState(false);
	const [ searchResults, setSearchResults ] = useState<SearchResult[]>([]);
	const [ statusFilterSelection, setStatusFilterSelection ] = useState<number[]>([]);
	const [ openClosedFilterSelection, setOpenClosedFilterSelection ] = useState<boolean | undefined>(undefined);
	const [ collectionStatuses, setCollectionStatuses ] = useState<CollectionStatus[]>([]);
	const [ columnOptionsSelection, setColumnOptionsSelection ] = useState<number[]>([]);
	const [ searchBoxValue, setSearchBoxValue ] = useState('');
	const [ searchBoxError, setSearchBoxError ] = useState(false);
	const [ searchStatus, setSearchStatus ] = useState(searchStatuses.NONE);
	const [ errorMessageList, setErrorMessageList ] = useState<string[]>(showUploadFilesMessage ? [ 'Please search for an account to upload documents to a specific account' ] : []);

	/// Local Variables
	const filteredColumnOptions = columnOptions.filter(x => columnOptionsSelection.includes(x.id));

	/// Effects
	useEffect(() => {
		API.get('settings/client-portal/customer/status') // TODO: change this to a post and filter by open/closed
			.then(data => {
				setCollectionStatuses(data.data.map(x => ({
					id: x.id,
					description: x.description,
					type: x.type,
					isClosed: x.isClosed
				})));
			})
			.catch(error => console.error('Error', error));
	}, [openClosedFilterSelection]);

	// Event Handlers
	const handleSearchButtonClick = e => {
		e.preventDefault();

		if (stringIsNullOrUndefinedOrBlank(searchBoxValue)) {
			setSearchBoxError(true);
			setErrorMessageList([ 'Please enter your search criteria' ]);
		}
		else {
			setSearchStatus(searchStatuses.SEARCHING);
			setErrorMessageList([]);

			API.post('client-portal/search/account', {
				pin: 310,
				searchCriteria: searchBoxValue,
				clientId: 51718,
				collectionStatusDescriptionIds: statusFilterSelection,
				isClosed: openClosedFilterSelection
			})
				.then(data => {
					setSearchResults(data.data.map(x => ({
						acctNumber: x.accountNumber,
						name: x.isCompany ? x.companyName : `${x.firstName} ${x.lastName}`,
						status: `${x.collectionStatusDescription} [${x.collectionStatusType}]`,
						isClosed: x.isClosed
					})));
					setSearchStatus(searchStatuses.FOUND_RESULTS);
				})
				.catch(error => console.error('Error', error));
		}
	};

	const handleFilterButtonClick = e => {
		setFiltersOpen(prevState => !prevState);
		setErrorMessageList([]);
	};

	const handleAcctRowClick = acctID =>
		history.push(generatePathForceParams(match.path, { acctID: acctID }));

	const handleStatusFilterChange = (e, { value }:DropdownProps) =>
		setStatusFilterSelection(value as number[]);

	const handleOpenClosedFilterChange = (e, { value }:DropdownProps) => {
		setOpenClosedFilterSelection(value as boolean | undefined);

		// Make sure selected statuses are compatible with the open/closed filter selection
		if (value !== undefined)
			setStatusFilterSelection(prevState => prevState.filter(x => collectionStatuses.find(y => y.id === x)?.isClosed === value));
	};

	const handleColumnOptionCheckboxChange = (e, { value, checked }: CheckboxProps) => {
		if (checked) {
			setColumnOptionsSelection(prevState => ([
				...prevState
			].concat(parseInt(value as string))));
		}
		else {
			setColumnOptionsSelection(prevState => prevState.filter((val, index, arr) => {
				return val != parseInt(value as string);
			}));
		}
	};

	const handleSearchBoxChange = (e, { value }) => {
		setSearchBoxValue(value as string);
		setSearchBoxError(false);
	};

	const handleUploadFilesMessageDismiss = index =>
		setErrorMessageList(prevState => removeElementFromArray(prevState, index));

	// Render
	return (
		<>
			{ visible &&
				<div
					style={{
						display: 'flex',
						flexDirection: 'column'
					}}
				>
					<div style={{
						textAlign: 'center',
						padding: '2em 0',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center'
					}}>
						{/* Search Bar */}
						<form onSubmit={handleSearchButtonClick}>
							<Input
								action={!searchBoxError && {
									icon: 'search',
									size: 'large'
								}}
								placeholder="Account Search..."
								style={{
									width: '30em'
								}}
								icon={searchBoxError && 'search'}
								value={searchBoxValue}
								onChange={handleSearchBoxChange}
								error={searchBoxError}
							/>
						</form>

						{/* Filter Button */}
						<Button
							basic
							compact
							icon
							borderless
							onClick={handleFilterButtonClick}
							active={filtersOpen}
							style={{ marginLeft: '.4em' }}
						>
							<Icon
								name='filter'
								fitted
								size="big"
							/>
						</Button>
						
						{/* Column Settings Button */}
						<Popup
							basic
							flowing
							on="click"
							position="bottom center"
							trigger={<Button basic compact icon borderless><Icon className="setting" fitted size="big" /></Button>}
						>
							<Popup.Header>Column Options</Popup.Header>
							<Popup.Content>
								<div style={{ display: 'flex', flexDirection: 'column' }}>
									{columnOptions.map((x, index) =>
										<Checkbox
											key={x.id}
											value={x.id}
											label={x.name}
											style={{ marginBottom: '.5em' }}
											checked={columnOptionsSelection.includes(x.id)}
											onChange={handleColumnOptionCheckboxChange}
										/>
									)}
								</div>
							</Popup.Content>
						</Popup>
					</div>

					{ errorMessageList.length > 0 &&
						<div style={{ display: 'flex', flexDirection: 'column', marginBottom: '1em', alignItems: 'center' }} >
							<ErrorMessageList errorMessages={errorMessageList} onDismiss={handleUploadFilesMessageDismiss} />
						</div>
					}

					{/* Filter bar */}
					<div className='enable-height-transition'
						style={{
							backgroundColor: '#a5a5a5',
							display: 'flex',
							justifyContent: 'flex-end',
							alignItems: 'center',
							padding: '0 .4em',
							overflow: (filtersOpen ? 'visible' : 'hidden'),
							height: (filtersOpen ? '3.5em' : '0')
						}}
					>
						<Dropdown
							text="Account Status"
							multiple
							selection
							search
							options={collectionStatuses.filter(x => openClosedFilterSelection === undefined || openClosedFilterSelection === x.isClosed).map(x => ({ value: x.id, text: `${x.description} [${x.type}]` }))}
							style={{ marginRight: '.4em' }}
							value={statusFilterSelection}
							onChange={handleStatusFilterChange}
						/>
						<Dropdown
							selection
							options={openClosedOptions}
							value={openClosedFilterSelection}
							onChange={handleOpenClosedFilterChange}
							text={openClosedFilterSelection === undefined ? openClosedOptions.reduce((current, next) => next.value === undefined ? next : current).text : undefined}
						/>
					</div>

					{/* Search Results */}
					{ searchStatus === searchStatuses.FOUND_RESULTS ? (
						<div
							style={{
								borderTop: `2px solid ${secondaryColor}`
							}}
						>
							<Table celled selectable striped>
								<Table.Header>
									<Table.Row>
										<Table.HeaderCell>Name</Table.HeaderCell>
										<Table.HeaderCell>Account Number</Table.HeaderCell>
										{ filteredColumnOptions.map(x =>
											<Table.HeaderCell key={x.id}>{x.name}</Table.HeaderCell>
										)}
									</Table.Row>
								</Table.Header>
								<Table.Body>
									{searchResults.map(x =>
										<Table.Row
											key={x.acctNumber}
											style={{cursor: 'pointer'}}
											onClick={() => { handleAcctRowClick(x.acctNumber); }}
										>
											<Table.Cell>{x.name}</Table.Cell>
											<Table.Cell>{x.acctNumber}</Table.Cell>
											{ filteredColumnOptions.map(y =>
												<Table.Cell key={y.id}>{y.getValue(x)}</Table.Cell>
											)}
										</Table.Row>
									)}
								</Table.Body>
							</Table>
						</div>
					) : searchStatus === searchStatuses.SEARCHING ? (
						<div
							style={{
								borderTop: `2px solid ${secondaryColor}`,
								position: 'relative',
								height: '10em'
							}}
						>
							<Dimmer active inverted style={{ zIndex: 'unset' }}>
								<Loader size='massive'>Loading</Loader>
							</Dimmer>
						</div>
					) : (
						<></>
					)}
				</div>
			}
		</>
	);
};

const searchStatuses = {
	NONE: 0,
	SEARCHING: 1,
	FOUND_RESULTS: 2
};

const openClosedOptions = [
	{
		text: 'All [Open / Closed]',
		value: undefined,
		key: 0
	},
	{
		text: 'Open',
		value: false,
		key: 1
	},
	{
		text: 'Closed',
		value: true,
		key: 2
	}
];

const columnOptions:ColumnOption[] = [
	{
		id: 1,
		name: 'Account Status',
		getValue: seachResult => seachResult.status
	},
	{
		id: 2,
		name: 'Open / Closed',
		getValue: seachResult => seachResult.isClosed ? 'Closed' : 'Open'
	}
];

interface ColumnOption {
	id: number,
	name: string,
	getValue: (SearchResult) => any
}

interface SearchResult {
	name: string,
	acctNumber: string,
	status: string,
	closed: boolean
}

interface SearchPageProps {
	showUploadFilesMessage?: boolean,
	visible?: boolean
}

export default SearchPage;